/**
 * URL Builder Utilities
 * Provides consistent URL building patterns across the Console application
 */

const REMOVED_FILTER_KEYS = ['topic', 'intent'];

export function stripRemovedFilterParams(searchParams) {
  if (!searchParams || typeof searchParams.entries !== 'function') {
    return null;
  }
  const params = new URLSearchParams(searchParams);
  let changed = false;
  for (const key of REMOVED_FILTER_KEYS) {
    if (params.has(key)) {
      params.delete(key);
      changed = true;
    }
  }
  return changed ? params : null;
}

/**
 * Builds entity detail URLs following the pattern: /[entity]/[entityId]?[context]
 * 
 * @param {string} entityType - The entity type (e.g., 'agents', 'traces', 'sessions')
 * @param {string} entityId - The entity identifier (will be URL encoded)
 * @param {Object} context - Context parameters
 * @param {string} context.timeRange - Time window (required, e.g., '24h', '7d')
 * @param {string} [context.tab] - Optional tab name
 * @param {Object} [context.filters] - Optional filters object
 * @param {string} [context.sort] - Optional sort parameter
 * @returns {string} Complete URL with query parameters
 */
export const buildEntityDetailURL = (entityType, entityId, context) => {
  if (!entityType) throw new Error('entityType is required');
  if (!entityId) throw new Error('entityId is required');
  if (!context?.timeRange) throw new Error('context.timeRange is required');

  const params = new URLSearchParams();
  
  // Time range is mandatory
  params.set('range', context.timeRange);
  
  // Optional tab
  if (context.tab) {
    params.set('tab', context.tab);
  }
  
  // Optional filters
  if (context.filters) {
    Object.entries(context.filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params.set(`filter.${key}`, value);
      }
    });
  }
  
  // Optional sort
  if (context.sort) {
    params.set('sort', context.sort);
  }
  
  const encodedId = encodeURIComponent(entityId);
  return `/${entityType}/${encodedId}?${params.toString()}`;
};

/**
 * Convenience function specifically for trace detail URLs
 * 
 * @param {string} traceId - Trace identifier
 * @param {string} timeRange - Time window
 * @param {Object} [options] - Optional context
 * @returns {string} Trace detail URL
 */
export const buildTraceDetailURL = (traceId, timeRange, options = {}) => {
  return buildEntityDetailURL('traces', traceId, {
    timeRange,
    tab: options.tab || 'spans', // Default tab for traces
    filters: options.filters,
    sort: options.sort
  });
};

/**
 * Convenience function specifically for session detail URLs
 * 
 * @param {string} sessionId - Session identifier
 * @param {string} timeRange - Time window
 * @param {Object} [options] - Optional context
 * @returns {string} Session detail URL
 */
export const buildSessionDetailURL = (sessionId, timeRange, options = {}) => {
  return buildEntityDetailURL('sessions', sessionId, {
    timeRange,
    tab: options.tab || 'overview', // Default tab for sessions
    filters: options.filters,
    sort: options.sort
  });
};

/**
 * ============================================================================
 * URL Filter Serialization (Phase 1 - Name-based filtering)
 * ============================================================================
 */

/**
 * Serialize filter object to URL query string
 * @param {Object} filters - Filter object with camelCase keys
 * @returns {string} Query string (without leading '?')
 */
export function serializeFilters(filters) {
  if (!filters || typeof filters !== 'object') {
    return '';
  }

  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    // Skip null, undefined, empty string, or false boolean values
    if (value === null || value === undefined || value === '' || value === false) {
      return;
    }

    // Handle arrays (comma-separated)
    if (Array.isArray(value)) {
      if (value.length > 0) {
        params.set(key, value.join(','));
      }
      return;
    }

    // Handle booleans (only add if true)
    if (typeof value === 'boolean') {
      if (value === true) {
        params.set(key, 'true');
      }
      return;
    }

    // Handle strings and numbers
    params.set(key, String(value));
  });

  const queryString = params.toString();

  // Development warning for long URLs
  if (process.env.NODE_ENV === 'development' && queryString.length > 1500) {
    console.warn(
      `⚠️ URL Filter Warning: Query string length is ${queryString.length} characters (recommended: <2000)\n` +
      `Current URL: ?${queryString}\n` +
      `Consider reducing active filters or reviewing filter values.`
    );
  }

  return queryString;
}

/**
 * Deserialize URL query params to filter object
 * @param {URLSearchParams} searchParams - React Router's URLSearchParams
 * @returns {Object} Filter object with camelCase keys
 */
export function deserializeFilters(searchParams) {
  if (!searchParams || typeof searchParams.entries !== 'function') {
    return {};
  }

  const filters = {};

  for (const [key, value] of searchParams.entries()) {
    if (REMOVED_FILTER_KEYS.includes(key)) {
      continue;
    }

    // Skip empty values
    if (!value || value === '') {
      continue;
    }

    // Handle boolean values
    if (value === 'true') {
      filters[key] = true;
      continue;
    }
    if (value === 'false') {
      filters[key] = false;
      continue;
    }

    // Handle comma-separated arrays
    if (value.includes(',')) {
      filters[key] = value.split(',').map(v => v.trim()).filter(v => v.length > 0);
      continue;
    }

    // Handle numbers
    const numberValue = Number(value);
    if (!isNaN(numberValue) && value === String(numberValue)) {
      filters[key] = numberValue;
      continue;
    }

    // Default: string value
    filters[key] = value;
  }

  return filters;
}

/**
 * Get a single filter value from URL params with type coercion
 * @param {URLSearchParams} searchParams - React Router's URLSearchParams
 * @param {string} key - Filter key to retrieve
 * @param {*} defaultValue - Default value if not found
 * @returns {*} Filter value with proper type
 */
export function getFilterValue(searchParams, key, defaultValue = null) {
  if (!searchParams || !searchParams.has(key)) {
    return defaultValue;
  }

  const value = searchParams.get(key);

  // Handle boolean
  if (value === 'true') return true;
  if (value === 'false') return false;

  // Handle comma-separated array
  if (value.includes(',')) {
    return value.split(',').map(v => v.trim()).filter(v => v.length > 0);
  }

  // Handle number
  const numberValue = Number(value);
  if (!isNaN(numberValue) && value === String(numberValue)) {
    return numberValue;
  }

  // Return string
  return value;
}

/**
 * Update URL with new filter values (merge with existing)
 * @param {URLSearchParams} searchParams - Current URLSearchParams
 * @param {Function} setSearchParams - React Router's setSearchParams function
 * @param {Object} updates - Filter updates to apply
 */
export function updateFilters(searchParams, setSearchParams, updates) {
  if (!updates || typeof updates !== 'object') {
    return;
  }

  const currentFilters = deserializeFilters(searchParams);
  const newFilters = { ...currentFilters, ...updates };

  // Remove keys with null/undefined/false values
  Object.keys(newFilters).forEach(key => {
    if (newFilters[key] === null || newFilters[key] === undefined || newFilters[key] === false || newFilters[key] === '') {
      delete newFilters[key];
    }
  });

  const queryString = serializeFilters(newFilters);
  setSearchParams(queryString);
}

/**
 * Clear specific filters from URL
 * @param {URLSearchParams} searchParams - Current URLSearchParams
 * @param {Function} setSearchParams - React Router's setSearchParams function
 * @param {string[]} keys - Filter keys to remove
 */
export function clearFilters(searchParams, setSearchParams, keys) {
  if (!Array.isArray(keys) || keys.length === 0) {
    return;
  }

  const currentFilters = deserializeFilters(searchParams);

  keys.forEach(key => {
    delete currentFilters[key];
  });

  const queryString = serializeFilters(currentFilters);
  setSearchParams(queryString);
}

/**
 * Clear all filters from URL
 * @param {Function} setSearchParams - React Router's setSearchParams function
 */
export function clearAllFilters(setSearchParams) {
  setSearchParams('');
}
